/**
 * The shell's authored moments.
 *
 * The menu had no motion at all beyond CSS hover transitions, which is why every screen read
 * as a static document however well it was lit: nothing in it ever acknowledged that something
 * had happened. A claim, a purchase, a level's worth of XP and an equip all resolved by a
 * number silently changing value.
 *
 * Three primitives, and deliberately only three — the quality floor is "one authored moment,
 * not scattered effects", so this is not an animation library, it is the four or five places a
 * game menu owes the player a reaction:
 *
 *   - `countUp` — a figure that changed counts to its new value instead of teleporting.
 *   - `rewardBurst` — the one celebratory beat, over the control that caused it.
 *   - `revealOnMount` — a single staggered rise as a screen arrives.
 *   - `bootIn` — the front door's power-on: a panel slides in with a two-step flicker, the way
 *     hardware comes up, reserved for /play's orchestrated entrance.
 *
 * ## Two rules everything here obeys
 *
 * **Motion starts from the already-visible state.** No `opacity: 0` initial class anywhere. If
 * a script throws, a stylesheet fails to load, or the Web Animations API is missing, the page
 * is a static page with all of its content on screen — never a blank one. This is the reason
 * `revealOnMount` animates a transform rather than fading in: a half-applied fade leaves
 * content invisible, and a half-applied translate leaves it slightly in the wrong place.
 *
 * **`prefers-reduced-motion` removes the movement, never the information.** A count-up becomes
 * an assignment, a burst becomes nothing, a reveal becomes a no-op. The number still arrives.
 */

import { formatNumber } from '../i18n';

/** One read of the OS setting, per call rather than cached — a person can change it mid-session. */
function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/** jsdom has no `element.animate`; so do some older embedded webviews. */
function canAnimate(el: Element): boolean {
  return typeof (el as HTMLElement).animate === 'function';
}

/**
 * Exponential ease-out, as a `cubic-bezier` — the ease the quality floor asks for.
 *
 * Fast departure, long settle. A linear or ease-in-out move on a UI element reads as a slide
 * transition; this reads as something arriving under its own weight and stopping.
 */
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';

export interface CountUpOptions {
  /** Milliseconds. Clamped below — a long count on a small delta is a wait, not a flourish. */
  durationMs?: number;
  /** Formats each intermediate value. Defaults to a thousands-separated integer. */
  format?: (value: number) => string;
}

/**
 * Counts `el`'s text from `from` to `to`.
 *
 * Why a count and not a swap: the number a player cares about on this screen is almost always
 * a *balance*, and a balance that jumps from 750 to 250 tells them the purchase went through
 * while a balance that counts down tells them what it cost. The motion carries the delta,
 * which is the part a static number cannot.
 *
 * Duration scales with the size of the change and is capped: +40 credits should feel instant,
 * +5,000 should be worth watching, and neither should take a second and a half.
 */
export function countUp(el: HTMLElement, from: number, to: number, opts: CountUpOptions = {}): void {
  const format = opts.format ?? ((value: number) => formatNumber(Math.round(value)));
  if (from === to || reducedMotion() || typeof requestAnimationFrame !== 'function') {
    el.textContent = format(to);
    return;
  }

  const delta = Math.abs(to - from);
  const duration = opts.durationMs ?? Math.min(900, 260 + Math.sqrt(delta) * 22);

  // The clock is the FIRST FRAME'S OWN timestamp, not `performance.now()` read before the loop
  // starts. `requestAnimationFrame` is only specified to hand its callback a
  // `DOMHighResTimeStamp`; it is not specified to share `performance.now()`'s time origin, and
  // where it does not (jsdom, and some webviews) the elapsed time comes out negative, the ease
  // goes negative with it, and a balance counting up to 137 renders "-137,295" instead. Taking
  // both readings from the same source removes the assumption entirely.
  let start: number | null = null;

  const step = (now: number) => {
    start ??= now;
    // Clamped at BOTH ends. The upper clamp ends the animation; the lower one is the guard
    // against a clock that hands back a timestamp earlier than the one before it.
    const t = Math.min(1, Math.max(0, (now - start) / duration));
    // Same easing curve as the transforms, so a number and the panel it sits in settle
    // together rather than on two different clocks.
    const eased = 1 - (1 - t) ** 3;
    el.textContent = format(from + (to - from) * eased);
    if (t < 1) requestAnimationFrame(step);
    else el.textContent = format(to);
  };
  requestAnimationFrame(step);
}

export interface RewardBurstOptions {
  /** Shown rising out of the burst — "+400", "Equipped", a skin's name. */
  label?: string;
  /** `gold` for currency, `cyan` for a confirmation, `violet` for something rare. */
  tone?: 'gold' | 'cyan' | 'violet';
}

/**
 * The one celebration: a ring expanding out of `anchor`, with an optional label rising off it.
 *
 * Anchored to the control that caused it rather than played centre-screen, because a burst in
 * the middle of the viewport is a cutscene — it interrupts, it has to be dismissed or waited
 * out, and it arrives detached from the thing the player just pressed. Over the button, it is
 * feedback: it says *that* did *this*, and it is gone before the hand leaves the mouse.
 *
 * Appended to `document.body` at a fixed position computed from the anchor's box, so it is
 * never clipped by an `overflow-hidden` panel the button lives inside — which is
 * exactly what would happen to an absolutely-positioned child.
 */
export function rewardBurst(anchor: Element, opts: RewardBurstOptions = {}): void {
  if (reducedMotion() || !canAnimate(anchor)) return;

  const box = anchor.getBoundingClientRect();
  if (box.width === 0 && box.height === 0) return;
  const centreX = box.left + box.width / 2;
  const centreY = box.top + box.height / 2;

  const tone = opts.tone ?? 'gold';
  const colour =
    tone === 'cyan' ? 'var(--color-cyan)' : tone === 'violet' ? 'var(--color-purple-light)' : 'var(--color-gold)';

  const layer = document.createElement('div');
  layer.dataset.fx = 'reward-burst';
  layer.setAttribute('aria-hidden', 'true');
  layer.className = 'pointer-events-none fixed z-[60]';
  layer.style.left = `${centreX}px`;
  layer.style.top = `${centreY}px`;
  document.body.append(layer);

  // The ring. A border rather than a filled circle: a filled disc over a button hides the
  // thing the player is looking at for the length of the animation.
  const ring = document.createElement('div');
  ring.className = 'absolute rounded-full';
  ring.style.cssText = [
    'left:0',
    'top:0',
    'width:16px',
    'height:16px',
    'margin-left:-8px',
    'margin-top:-8px',
    `border:2px solid ${colour}`,
    `box-shadow:0 0 12px ${colour}`,
  ].join(';');
  layer.append(ring);

  const RING_MS = 520;
  ring.animate(
    [
      { transform: 'scale(0.6)', opacity: 0.95 },
      { transform: `scale(${Math.max(3.2, box.width / 14)})`, opacity: 0 },
    ],
    { duration: RING_MS, easing: EASE_OUT, fill: 'forwards' },
  );

  // Four short spokes, on the diagonals. Not eight, not a particle system: this is a menu
  // acknowledging a click, and past about four moving parts a burst stops reading as one
  // gesture and starts reading as an effect someone was pleased with.
  for (const angle of [45, 135, 225, 315]) {
    const spoke = document.createElement('div');
    spoke.className = 'absolute';
    spoke.style.cssText = [
      'left:0',
      'top:0',
      'width:2px',
      'height:9px',
      `background:${colour}`,
      `transform:rotate(${angle}deg) translateY(-10px)`,
      'transform-origin:50% 100%',
    ].join(';');
    layer.append(spoke);
    spoke.animate(
      [
        { transform: `rotate(${angle}deg) translateY(-10px) scaleY(0.4)`, opacity: 1 },
        { transform: `rotate(${angle}deg) translateY(-30px) scaleY(1)`, opacity: 0 },
      ],
      { duration: 420, easing: EASE_OUT, fill: 'forwards' },
    );
  }

  let lifetime = RING_MS;

  if (opts.label) {
    const label = document.createElement('div');
    label.className =
      'absolute font-heading font-extrabold italic uppercase tracking-tight text-[15px] whitespace-nowrap';
    label.style.cssText = [
      'left:0',
      'top:0',
      'transform:translate(-50%,-50%)',
      `color:${colour}`,
      'text-shadow:2px 2px 0 rgba(0,0,0,0.9)',
    ].join(';');
    label.textContent = opts.label;
    layer.append(label);
    // Rises further and lingers longer than the ring — the ring is the *event*, the label is
    // the *amount*, and the amount is the part worth reading.
    label.animate(
      [
        { transform: 'translate(-50%,-50%) scale(0.85)', opacity: 0 },
        { transform: 'translate(-50%,-160%) scale(1)', opacity: 1, offset: 0.25 },
        { transform: 'translate(-50%,-260%) scale(1)', opacity: 0 },
      ],
      { duration: 1100, easing: EASE_OUT, fill: 'forwards' },
    );
    lifetime = 1100;
  }

  window.setTimeout(() => layer.remove(), lifetime + 60);
}

export interface RevealOptions {
  /** Milliseconds between successive children. Kept small — this is a rise, not a cascade. */
  staggerMs?: number;
  /** How far each element travels, in px. */
  distance?: number;
}

/**
 * A screen arriving: each child rises a few pixels into place, one shortly after the next.
 *
 * One entrance for the route, applied to one container's direct children — not the same
 * entrance re-declared on every section, which is the tell that motion was added per-component
 * rather than composed. Fourteen pixels and 40ms apart: enough that the eye reads an order to
 * the screen, far short of a page that has to finish assembling before it can be used.
 *
 * The elements are never hidden. They start where they end up and are animated *from* an
 * offset, so at any point in the animation — including "the API is not implemented and nothing
 * ran" — every one of them is on screen and legible.
 */
export function revealOnMount(container: HTMLElement, opts: RevealOptions = {}): void {
  if (reducedMotion()) return;
  const stagger = opts.staggerMs ?? 40;
  const distance = opts.distance ?? 14;

  const children = Array.from(container.children).filter((child): child is HTMLElement =>
    child instanceof HTMLElement,
  );
  // Past a handful, the last element's delay is long enough to read as the page being slow.
  const animated = children.slice(0, 8);

  animated.forEach((child, index) => {
    if (!canAnimate(child)) return;
    child.animate(
      [
        { transform: `translateY(${distance}px)`, opacity: 0.35 },
        { transform: 'translateY(0)', opacity: 1 },
      ],
      { duration: 460, delay: index * stagger, easing: EASE_OUT, fill: 'backwards' },
    );
  });
}


export interface BootInOptions {
  /** Horizontal arrival direction: negative slides in from the left, positive from the right. */
  fromX?: number;
  /** Vertical arrival offset — PLAY lands, panels slide. */
  fromY?: number;
  delayMs?: number;
  durationMs?: number;
  /** A touch over 1 makes the element overshoot and settle — the "lands with weight" variant. */
  overshoot?: boolean;
}

/**
 * A power-on entrance: the element arrives from `fromX/fromY` with a two-dip opacity flicker
 * before settling — the way a CRT or an avionics panel comes up, which is the register the
 * whole shell is drawn in. Same safety contract as everything here: the element is ALREADY
 * visible and in place; reduced motion or a missing WAAPI means it simply stays that way.
 */
export function bootIn(el: HTMLElement, opts: BootInOptions = {}): void {
  if (reducedMotion() || !canAnimate(el)) return;
  const fromX = opts.fromX ?? 0;
  const fromY = opts.fromY ?? 0;
  const scaleFrom = opts.overshoot ? 1.03 : 1;
  el.animate(
    [
      { transform: `translate(${fromX}px, ${fromY}px) scale(${scaleFrom})`, opacity: 0, offset: 0 },
      { transform: `translate(${fromX * 0.4}px, ${fromY * 0.4}px) scale(${scaleFrom})`, opacity: 0.75, offset: 0.35 },
      { transform: `translate(${fromX * 0.25}px, ${fromY * 0.25}px) scale(${(1 + scaleFrom) / 2})`, opacity: 0.4, offset: 0.5 },
      { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 1 },
    ],
    { duration: opts.durationMs ?? 520, delay: opts.delayMs ?? 0, easing: EASE_OUT, fill: 'backwards' },
  );
}
